import { useEffect, useMemo, useRef, useState } from 'react';
import type { Expense } from '@stfors/shared-types';
import { RefreshCw, Undo2, Wallet } from 'lucide-react';
import {
  useUpdateExpense,
  useDeleteExpense,
  useApproveExpense,
  useReimburseExpense,
} from '../api/queries/expenses';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { useProjects } from '../api/queries/projects';
import { ProjectOptions } from './ProjectOptions';
import { MatterSelect } from './MatterSelect';
import { useAssets } from '../api/queries/assets';
import { useUploadDocument } from '../api/queries/documents';
import { FileDrop } from './FileDrop';
import { useVendors } from '../api/queries/vendors';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { expenseStatusPills } from '../utils/expense-status';
import { can } from '../auth/store';
import { fmtDate, fmtDateTime } from '../utils/date';
import { Modal } from './Modal';
import { IconAction } from './IconAction';
import { assetOptionLabel } from '../utils/asset';
import { DateInput } from './DateInput';
import { MoneyInput } from './MoneyInput';
import { VendorPicker } from './VendorPicker';
import {
  DocumentPreviewModal,
  type PreviewableFile,
} from './DocumentPreviewModal';
import './ExpenseDetailModal.css';

import { formatMoney } from '../utils/money';
import { expenseTypeLabel, tracksMileage } from '../utils/expense';
import {
  receiptFilename, receiptMime,
  buildReceiptFilename, extensionOf, normalizeReceiptFile, renameFile,
} from '../utils/receipt';
import { Toggle } from './Toggle';

export interface ExpenseDetailModalProps {
  expense: Expense;
  onClose: () => void;
  /** Open the "set this expense to repeat" flow. */
  onRepeat: (expense: Expense) => void;
  /** "Duplicate and edit": open New Expense pre-filled from this one. */
  onDuplicate?: (expense: Expense) => void;
}

/**
 * Click-through detail + edit for a single expense. All row actions
 * (approve, reimburse, save, repeat, delete) live here so the list table
 * stays read-only and scannable.
 */
export function ExpenseDetailModal({
  expense,
  onClose,
  onRepeat,
  onDuplicate,
}: ExpenseDetailModalProps): JSX.Element {
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();
  const approveExpense = useApproveExpense();
  const reimburseExpense = useReimburseExpense();
  const lookups = useLookups();
  const { data: projectsData } = useProjects();
  const assetsQ = useAssets();

  const expenseTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'expense_type');
  const taxCategoryOptions = activeOptionsFor(lookups.data?.lookup_options, 'tax_category');
  const projects = projectsData?.projects ?? [];
  const assets = assetsQ.data?.assets ?? [];

  const billed = !!expense.billed;
  const canWrite = can('expense.write') && !billed;
  const [editing, setEditing] = useState(false);

  // Form state — reset when the expense id changes or edit is cancelled.
  const [projectId, setProjectId] = useState<number | 'none'>(
    expense.project_id === null ? 'none' : expense.project_id,
  );
  const [type, setType] = useState(expense.type);
  const [dateEpoch, setDateEpoch] = useState<number | null>(expense.date);
  const [amount, setAmount] = useState(expense.amount);
  const [taxAmount, setTaxAmount] = useState(expense.tax_amount ?? '0');
  const [vendorId, setVendorId] = useState<number | null>(expense.vendor_id);
  const [vendorText, setVendorText] = useState(expense.vendor ?? '');
  const [receiptNumber, setReceiptNumber] = useState(expense.receipt_number ?? '');
  const [taxCategory, setTaxCategory] = useState(expense.tax_category ?? '');
  const [mileage, setMileage] = useState(expense.mileage ?? '');
  const [reimbursable, setReimbursable] = useState(expense.reimbursable);
  const [notes, setNotes] = useState(expense.notes ?? '');
  const [assetId, setAssetId] = useState<number | null>(expense.asset_id ?? null);
  // A receipt to attach (or to replace the attached one) on save. Uploaded
  // first, the same way New Expense does it, then its vault path is PATCHed.
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const uploadDoc = useUploadDocument();
  // The project's matter this cost belongs to (0061).
  const [matterId, setMatterId] = useState<number | null>(expense.matter_id ?? null);
  const [error, setError] = useState<string | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<PreviewableFile | null>(null);

  // The tax category the CURRENT vendor would supply. <VendorPicker> runs the
  // same query under the same key, so this is a cache read, not a second fetch.
  const vendorsQ = useVendors();
  const vendorDefaultTax = useMemo(() => {
    if (vendorId === null) return '';
    return vendorsQ.data?.vendors.find((v) => v.id === vendorId)?.default_tax_category ?? '';
  }, [vendorsQ.data, vendorId]);

  // The default applies on OPEN, not only when a vendor is picked. An expense
  // recorded by a form that never collected the field (every travel cost until
  // e58b236) has a vendor but no category, and the box read "—" directly under
  // a hint naming the default it should have had.
  //
  // Keyed on the default alone: the vendor list resolves after mount, and
  // switching vendors offers the new one's default. Reading `taxCategory`
  // through a ref keeps it OUT of the deps, so clearing the box back to "—"
  // stays cleared instead of being refilled on the next render.
  const liveTaxCategory = useRef(taxCategory);
  liveTaxCategory.current = taxCategory;
  useEffect(() => {
    if (vendorDefaultTax !== '' && liveTaxCategory.current === '') {
      setTaxCategory(vendorDefaultTax);
    }
  }, [vendorDefaultTax]);

  // Sync form when list data refreshes (e.g. after approve) while not mid-edit.
  useEffect(() => {
    if (editing) return;
    setProjectId(expense.project_id === null ? 'none' : expense.project_id);
    setType(expense.type);
    setDateEpoch(expense.date);
    setAmount(expense.amount);
    setTaxAmount(expense.tax_amount ?? '0');
    setVendorId(expense.vendor_id);
    setVendorText(expense.vendor ?? '');
    setReceiptNumber(expense.receipt_number ?? '');
    setTaxCategory(expense.tax_category ?? vendorDefaultTax);
    setMileage(expense.mileage ?? '');
    setReimbursable(expense.reimbursable);
    setNotes(expense.notes ?? '');
    setAssetId(expense.asset_id ?? null);
    setMatterId(expense.matter_id ?? null);
  }, [expense, editing, vendorDefaultTax]);

  const busy =
    updateExpense.isPending
    || deleteExpense.isPending
    || approveExpense.isPending
    || reimburseExpense.isPending
    || uploadDoc.isPending;

  const resetFromExpense = () => {
    setProjectId(expense.project_id === null ? 'none' : expense.project_id);
    setType(expense.type);
    setDateEpoch(expense.date);
    setAmount(expense.amount);
    setTaxAmount(expense.tax_amount ?? '0');
    setVendorId(expense.vendor_id);
    setVendorText(expense.vendor ?? '');
    setReceiptNumber(expense.receipt_number ?? '');
    // Cancel returns the form to how it OPENED, which includes the default.
    setTaxCategory(expense.tax_category ?? vendorDefaultTax);
    setMileage(expense.mileage ?? '');
    setReimbursable(expense.reimbursable);
    setNotes(expense.notes ?? '');
    setAssetId(expense.asset_id ?? null);
    setMatterId(expense.matter_id ?? null);
    setReceiptFile(null);
    setError(null);
    setEditing(false);
  };

  const onSave = async () => {
    setError(null);
    if (dateEpoch === null) {
      setError('Pick a date.');
      return;
    }
    const amt = Number(amount);
    const tax = Number(taxAmount || '0');
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter a positive total amount.');
      return;
    }
    if (!Number.isFinite(tax) || tax < 0) {
      setError('Sales tax must be zero or more.');
      return;
    }
    if (tax > amt) {
      setError('Sales tax cannot exceed the total amount.');
      return;
    }
    if (!type.trim()) {
      setError('Pick a type.');
      return;
    }

    try {
      // Receipt first: if the upload fails, nothing about the expense changes.
      let receiptPath: string | undefined;
      if (receiptFile) {
        const pid = projectId === 'none' ? null : projectId;
        const normalized = await normalizeReceiptFile(receiptFile);
        const named = renameFile(normalized, buildReceiptFilename({
          caseNumber: projects.find((p) => p.id === pid)?.case_number ?? null,
          dateEpoch,
          receiptNumber: receiptNumber.trim() || null,
          vendor: vendorText.trim() || expense.vendor || '',
          type: type.trim(),
          ext: extensionOf(normalized),
        }));
        const resp = await uploadDoc.mutateAsync({
          file: named,
          document_type: 'other',
          convert_to_pdf: true,
          ...(pid !== null ? { project_id: pid } : {}),
        });
        receiptPath = resp.document.file_path;
      }
      await updateExpense.mutateAsync({
        id: expense.id,
        ...(receiptPath ? { receipt_path: receiptPath } : {}),
        type: type.trim(),
        amount: amt.toFixed(2),
        tax_amount: tax.toFixed(2),
        date: dateEpoch,
        project_id: projectId === 'none' ? null : projectId,
        vendor_id: vendorId,
        // When no picker selection, allow free-text vendor snapshot.
        vendor: vendorId === null ? (vendorText.trim() || null) : undefined,
        receipt_number: receiptNumber.trim() || null,
        tax_category: taxCategory.trim() || null,
        // Whatever is in the (visible) field. A type change to something that
        // doesn't track mileage empties it above, so there is no stale value to
        // guard against here — and gating on tracksMileage() would silently
        // wipe the miles on a legacy "Travel" row the moment it was saved.
        mileage: mileage.trim() ? Number(mileage).toFixed(2) : null,
        reimbursable,
        notes: notes.trim() || null,
        asset_id: assetId,
        matter_id: projectId === 'none' ? null : matterId,
      });
      setReceiptFile(null);
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Cannot edit a billed expense — void the invoice first.');
      } else if (err instanceof ApiError && err.status === 403) {
        setError("You can't edit expenses.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError('Invalid values — check amount, tax, and mileage.');
      } else {
        // Includes a failed receipt upload, which happens before the save.
        setError(apiErrorText(err, 'Could not save the expense.'));
      }
    }
  };

  const onDelete = () => {
    if (!confirm('Delete this expense?')) return;
    setError(null);
    deleteExpense.mutate(expense.id, {
      onSuccess: () => onClose(),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setError('Cannot delete a billed expense.');
        } else if (err instanceof ApiError && err.status === 403) {
          setError("You can't delete expenses.");
        } else {
          setError('Delete failed.');
        }
      },
    });
  };

  const onApproveToggle = () => {
    setError(null);
    approveExpense.mutate(
      { id: expense.id, approve: !expense.approved_at },
      { onError: () => setError('Approval action failed.') },
    );
  };

  const onReimburseToggle = () => {
    setError(null);
    const mark = !expense.reimbursed_at;
    reimburseExpense.mutate(
      { id: expense.id, reimburse: mark },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            const msg = (err.body as { message?: string } | null)?.message;
            setError(msg ?? (mark ? 'Cannot reimburse.' : 'Cannot clear reimbursement.'));
          } else if (err instanceof ApiError && err.status === 403) {
            setError("You can't change reimbursement status.");
          } else {
            setError(mark ? 'Reimburse failed.' : 'Could not undo reimbursement.');
          }
        },
      },
    );
  };

  const openReceipt = () => {
    if (!expense.receipt_path) return;
    setReceiptPreview({
      downloadUrl: `/api/expenses/${expense.id}/receipt`,
      filename: receiptFilename(expense),
      mimeType: receiptMime(expense),
    });
  };

  const projectLabel =
    expense.project_id === null
      ? 'General business'
      : (expense.project_case_number
        ? `${expense.project_case_number}${expense.project_title ? ` — ${expense.project_title}` : ''}`
        : `#${expense.project_id}`);

  // Edit + Delete sit in the header next to × (view mode only).
  const headerActions = !editing ? (
    <>
      {canWrite && onDuplicate && (
        <IconAction
          action="duplicate"
          onClick={() => onDuplicate(expense)}
          disabled={busy}
        />
      )}
      {canWrite && (
        <IconAction
          action="edit"
          onClick={() => { setError(null); setEditing(true); }}
          disabled={busy}
        />
      )}
      {can('expense.delete') && (
        <IconAction
          action="delete"
          onClick={onDelete}
          disabled={busy || billed}
          title={billed ? 'Delete (void the invoice first)' : 'Delete'}
        />
      )}
    </>
  ) : null;

  // Footer: icon actions when viewing; Cancel + Save when editing.
  // No Close — the header × covers that.
  const footer = editing ? (
    <div className="exdm-footer exdm-footer-edit">
      <IconAction action="cancel" onClick={resetFromExpense} disabled={busy} />
      <IconAction
        action="save"
        onClick={() => void onSave()}
        disabled={busy}
        label={updateExpense.isPending ? 'Saving…' : 'Save'}
      />
    </div>
  ) : (
    <div className="exdm-footer">
      <div className="exdm-footer-icons">
        {can('expense.write') && (
          <button
            type="button"
            className="icon-btn"
            onClick={() => {
              setError(null);
              onRepeat(expense);
            }}
            disabled={busy}
            title="Repeat"
            aria-label="Repeat"
          >
            <RefreshCw size={18} strokeWidth={1.75} aria-hidden />
          </button>
        )}
        {can('expense.approve') && (
          <IconAction
            action={expense.approved_at ? 'unapprove' : 'approve'}
            onClick={onApproveToggle}
            disabled={busy || !!expense.reimbursed_at}
            title={
              expense.reimbursed_at
                ? 'Already reimbursed'
                : (expense.approved_at ? 'Unapprove' : 'Approve')
            }
          />
        )}
        {can('expense.reimburse')
          && expense.reimbursable
          && expense.approved_at
          && !expense.reimbursed_at && (
          <button
            type="button"
            className="icon-btn icon-btn-cyan"
            onClick={onReimburseToggle}
            disabled={busy}
            title="Reimburse"
            aria-label="Reimburse"
          >
            <Wallet size={18} strokeWidth={1.75} aria-hidden />
          </button>
        )}
        {can('expense.reimburse') && expense.reimbursed_at && (
          <button
            type="button"
            className="icon-btn"
            onClick={onReimburseToggle}
            disabled={busy}
            title="Unreimburse — clear the reimbursed stamp if marked by mistake"
            aria-label="Unreimburse"
          >
            <Undo2 size={18} strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );

  // Hide empty footer when viewing with no permitted actions.
  const hasViewActions =
    can('expense.write')
    || can('expense.approve')
    || (can('expense.reimburse') && (
      expense.reimbursed_at
      || (expense.reimbursable && expense.approved_at)
    ));
  const showFooter = editing || hasViewActions;

  return (
    <>
      <Modal
        open
        title={editing ? 'Edit Expense' : 'Expense'}
        onClose={onClose}
        busy={busy}
        headerActions={headerActions}
        footer={showFooter ? footer : undefined}
      >
        {error && (
          <p className="exdm-error" role="alert">{error}</p>
        )}
        {billed && (
          <p className="exdm-banner">
            This expense is on an invoice — void the invoice before editing or deleting.
          </p>
        )}

        <div className="exdm-status-row">
          {/* Same rule as the Expenses table — utils/expense-status.ts. */}
          {expenseStatusPills(expense).map((p) => (
            <span
              key={p.label}
              className={`ex-pill${p.tone === 'neutral' ? '' : ` ex-pill-${p.tone}`}`}
              title={p.title}
            >
              {p.label}
            </span>
          ))}
          {expenseStatusPills(expense).length === 0 && (
            <span className="exdm-muted">No status flags</span>
          )}
        </div>

        {editing ? (
          <div className="exdm-form">
            <label className="exdm-field">
              <span>Project</span>
              <select
                className="form-input"
                value={projectId === 'none' ? 'none' : String(projectId)}
                onChange={(e) => {
                  const v = e.target.value;
                  setProjectId(v === 'none' ? 'none' : Number(v));
                  setMatterId(null); // a matter belongs to one project
                }}
                disabled={busy}
              >
                <option value="none">General business (no project)</option>
                <ProjectOptions projects={projects} />
              </select>
            </label>

            <MatterSelect
              className="exdm-field"
              projectId={projectId === 'none' ? null : projectId}
              value={matterId}
              onChange={setMatterId}
              disabled={busy}
            />

            <div className="exdm-row">
              <label className="exdm-field">
                <span>Type</span>
                <select
                  className="form-input"
                  value={type}
                  onChange={(e) => {
                    const next = e.target.value;
                    setType(next);
                    // Switching to a type that doesn't track mileage clears the
                    // field, which is also what hides it. Leaving a stale value
                    // behind would keep the box on screen (it stays visible
                    // while it holds anything, for legacy rows) and save miles
                    // against a hotel bill.
                    if (!tracksMileage(next)) setMileage('');
                  }}
                  disabled={busy}
                >
                  {expenseTypeOptions.map((o) => (
                    <option key={o.id} value={o.value}>{o.label ?? o.value}</option>
                  ))}
                  {type && !expenseTypeOptions.some((o) => o.value === type) && (
                    <option value={type}>{expenseTypeLabel(type)} (legacy)</option>
                  )}
                </select>
              </label>
              <label className="exdm-field">
                <span>Date</span>
                <DateInput value={dateEpoch} onChange={setDateEpoch} disabled={busy} />
              </label>
            </div>

            <div className="exdm-row">
              <label className="exdm-field">
                <span>Total amount (USD)</span>
                <MoneyInput value={amount} onChange={setAmount} disabled={busy} />
              </label>
              <label className="exdm-field">
                <span>Sales tax (USD)</span>
                <MoneyInput value={taxAmount} onChange={setTaxAmount} disabled={busy} />
              </label>
            </div>

            <label className="exdm-field">
              <span>Vendor</span>
              {/* The one-off name used to be a second input below the picker.
                  It lives inside the picker now (allowGeneric), alongside the
                  `generic_vendor` categories — same state, same send. */}
              {/* Picking a vendor doesn't fill the category here — the effect
                  above does, keyed on whichever vendor is selected, so the
                  already-had-a-vendor case and the just-picked-one case take
                  the same path. */}
              <VendorPicker
                value={vendorId}
                onChange={(id) => setVendorId(id)}
                allowGeneric
                textValue={vendorText}
                onTextChange={setVendorText}
                canAdd={can('vendor.write')}
                showTaxCategoryHint
                disabled={busy}
              />
            </label>

            <div className="exdm-row">
              <label className="exdm-field">
                <span>Receipt #</span>
                <input
                  type="text"
                  className="form-input"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  disabled={busy}
                  maxLength={120}
                />
              </label>
              <label className="exdm-field">
                <span>Tax category</span>
                <select
                  className="form-input"
                  value={taxCategory}
                  onChange={(e) => setTaxCategory(e.target.value)}
                  disabled={busy}
                >
                  <option value="">—</option>
                  {taxCategoryOptions.map((o) => (
                    <option key={o.id} value={o.value}>{o.label ?? o.value}</option>
                  ))}
                  {taxCategory
                    && !taxCategoryOptions.some((o) => o.value === taxCategory) && (
                    <option value={taxCategory}>{taxCategory} (legacy)</option>
                  )}
                </select>
              </label>
            </div>

            <div className="exdm-row">
              {/* Also shown when the row ALREADY has mileage: expenses recorded
                  before the type split (migration 0052) sit on plain "Travel"
                  and still carry miles. Hiding the field would make stored data
                  uneditable and invisible while it kept being submitted. */}
              {tracksMileage(type) || mileage.trim() !== '' ? (
                <label className="exdm-field">
                  <span>Mileage</span>
                  <input
                    type="text"
                    className="form-input"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                    placeholder="0.00"
                    disabled={busy}
                  />
                </label>
              ) : (
                <div />
              )}
              <Toggle
                checked={reimbursable}
                onChange={setReimbursable}
                label="Reimbursable to client"
                disabled={busy}
                className="exdm-field exdm-check"
              />
            </div>

            {can('asset.read') && (
              <label className="exdm-field">
                <span>Linked asset</span>
                <select
                  className="form-input"
                  value={assetId ?? 0}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setAssetId(n > 0 ? n : null);
                  }}
                  disabled={busy || assetsQ.isPending}
                >
                  <option value={0}>— None —</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {assetOptionLabel(a)}
                    </option>
                  ))}
                  {assetId != null && !assets.some((a) => a.id === assetId) && (
                    <option value={assetId}>
                      {expense.asset_name ?? `Asset #${assetId}`} (unavailable)
                    </option>
                  )}
                </select>
              </label>
            )}

            <label className="exdm-field">
              <span>Notes</span>
              <textarea
                className="form-input exdm-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={10000}
                disabled={busy}
              />
            </label>

            <div className="exdm-field">
              <span>Receipt</span>
              <FileDrop
                compact
                accept="image/*,application/pdf"
                disabled={busy}
                label={expense.receipt_path
                  ? `Drop a file to replace ${receiptFilename(expense)}`
                  : 'Drop the receipt here (PDF or image)'}
                filled={receiptFile?.name ?? null}
                onFiles={(files) => {
                  const f = files[0]!;
                  if (f.size > 25 * 1024 * 1024) {
                    setError('Receipt must be under 25 MB.');
                    return;
                  }
                  setError(null);
                  setReceiptFile(f);
                }}
              />
              {receiptFile && (
                <span className="exdm-hint">
                  Uploads when you save — stored as a PDF, named like every receipt.{' '}
                  <button type="button" className="btn-text" onClick={() => setReceiptFile(null)} disabled={busy}>
                    Remove
                  </button>
                </span>
              )}
            </div>
          </div>
        ) : (
          <dl className="exdm-dl">
            <div className="exdm-dl-row">
              <dt>Date</dt>
              <dd>{fmtDate(expense.date)}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Project</dt>
              <dd>
                {expense.project_id === null ? (
                  <span className="ex-general-business">General business</span>
                ) : (
                  <code>{projectLabel}</code>
                )}
              </dd>
            </div>
            {expense.matter_name && (
              <div className="exdm-dl-row">
                <dt>Matter</dt>
                <dd>{expense.matter_name}</dd>
              </div>
            )}
            <div className="exdm-dl-row">
              <dt>Type</dt>
              <dd>{expenseTypeLabel(expense.type)}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Amount</dt>
              <dd className="exdm-amount">{formatMoney(expense.amount)}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Sales tax</dt>
              <dd>{formatMoney(expense.tax_amount ?? '0')}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Vendor</dt>
              <dd>{expense.vendor ?? '—'}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Asset</dt>
              <dd>
                {expense.asset_id != null
                  ? (expense.asset_name ?? `Asset #${expense.asset_id}`)
                  : '—'}
              </dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Tax category</dt>
              <dd>{expense.tax_category ?? '—'}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Receipt #</dt>
              <dd>{expense.receipt_number ?? '—'}</dd>
            </div>
            {(tracksMileage(expense.type) || expense.mileage != null) && (
              <div className="exdm-dl-row">
                <dt>Mileage</dt>
                <dd>
                  {expense.mileage !== null && expense.mileage !== undefined
                    ? `${Number(expense.mileage).toFixed(2)} mi`
                    : '—'}
                </dd>
              </div>
            )}
            <div className="exdm-dl-row">
              <dt>Receipt file</dt>
              <dd>
                {expense.receipt_path ? (
                  <button type="button" className="exdm-receipt-link" onClick={openReceipt}>
                    {receiptFilename(expense)}
                  </button>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="exdm-dl-row exdm-dl-notes">
              <dt>Notes</dt>
              <dd>{expense.notes?.trim() ? expense.notes : '—'}</dd>
            </div>
            <div className="exdm-dl-row">
              <dt>Recorded</dt>
              <dd>
                {fmtDateTime(expense.created_at)}
                {expense.created_by_name ? ` · ${expense.created_by_name}` : ''}
              </dd>
            </div>
            {expense.approved_at && (
              <div className="exdm-dl-row">
                <dt>Approved</dt>
                <dd>{fmtDateTime(expense.approved_at)}</dd>
              </div>
            )}
            {expense.reimbursed_at && (
              <div className="exdm-dl-row">
                <dt>Reimbursed</dt>
                <dd>{fmtDateTime(expense.reimbursed_at)}</dd>
              </div>
            )}
          </dl>
        )}
      </Modal>

      {receiptPreview && (
        <DocumentPreviewModal
          file={receiptPreview}
          onClose={() => setReceiptPreview(null)}
        />
      )}
    </>
  );
}
